// Where each new payer came from: exactly one source, decided on their first split and kept on it.
// The first rule that matches wins, so a dinner link beats a code, a code beats a tagged link, and
// so on. Pure functions only (no disk, no network), so the web page can share the link helpers.

import { isSquare, nothingToPay, type PersonShare } from './money.ts';
import type { Split } from './types.ts';

export const SOURCES = ['group_thread', 'table_loop', 'referral', 'creator', 'restaurant', 'promo', 'share_card', 'returning_friend', 'sms', 'utm', 'direct'] as const;
export type PayerSource = (typeof SOURCES)[number];

/** How long a dinner's "Start a split" or "Next time" link still credits that dinner. */
export const LOOP_WINDOW_DAYS = 60;
/**
 * The scoreboard's "friends who become hosts within 30 days" stays 30 days. Kept apart from
 * LOOP_WINDOW_DAYS on purpose: a longer credit window must not stretch the metric.
 */
export const HOST_WITHIN_DAYS = 30;
const DAY = 86_400_000;

/** What `?src=` says about the link it's on. Every link the app makes carries one. */
export const LINK_SOURCES = ['next_time', 'start_split', 'pay_note', 'invite', 'group_share', 'dash', 'qr', 'share_card', 'referral', 'creator', 'promo', 'campaign', 'restaurant', 'sms_receipt', 'sms_reminder'] as const;
export type LinkSource = (typeof LINK_SOURCES)[number];
export const cleanSrc = (v: unknown): string => {
  const s = String(v ?? '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 30);
  return (LINK_SOURCES as readonly string[]).includes(s) ? s : '';
};

/** Adds ?src= to a link (relative or absolute), keeping any #fragment at the end. */
export function withSrc(link: string, src: LinkSource): string {
  const hash = link.indexOf('#');
  const [base, frag] = hash >= 0 ? [link.slice(0, hash), link.slice(hash)] : [link, ''];
  if (/[?&]src=/.test(base)) return link;
  return `${base}${base.includes('?') ? '&' : '?'}src=${src}${frag}`;
}

/** A group-text link older than this, tapped again, is the group coming back to it, not tonight's dinner. */
export const GROUP_RETURN_DAYS = 7;

/**
 * The group-share dinner a phone should remember after landing on a link. The newest link wins:
 * a group_share link sets it (the dinner in /s/CODE, else its from=), and any other link carrying
 * from= or src= clears it. A link with neither keeps what was there.
 */
export function nextGroupShare(prev: string, search: string, pathname: string): string {
  const q = new URLSearchParams(search);
  const src = cleanSrc(q.get('src'));
  const from = (q.get('from') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
  const inPath = /^\/s\/([A-Za-z0-9]{3,12})/.exec(pathname)?.[1]?.toUpperCase() || '';
  if (src === 'group_share') return inPath || (from.length >= 3 ? from : prev);
  if (from || q.has('src')) return '';
  return prev;
}

/** Everything the server knows when a payer makes their first split. */
export interface AttributionInput {
  /** The dinner whose link brought them, after self-referral was ruled out. */
  from?: { code: string; createdAt: string } | null;
  /**
   * The dinner a group_share link pointed at, with its created time looked up on the server (never
   * a time the phone sent), so an old link reads apart from one tapped the same night.
   */
  groupShare?: { code: string; createdAt: string } | null;
  /** The code on the split (already checked as active, and not their own). */
  code?: { code: string; kind: string; utm?: { source?: string; medium?: string } } | null;
  /** The x-ref header the phone sent: first touch, latest ?ref, ?from and ?src. */
  ref?: Record<string, unknown>;
  /** This payer's phone or device joined an earlier dinner as a friend. */
  wasFriend?: boolean;
  now?: number;
}

export interface Attribution { source: PayerSource; detail: string }

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v)).trim().slice(0, 100);

/** A dinner link counts for LOOP_WINDOW_DAYS after that dinner. */
export function inLoopWindow(fromCreatedAt: string, now = Date.now()): boolean {
  const t = Date.parse(fromCreatedAt);
  return Number.isFinite(t) && now - t <= LOOP_WINDOW_DAYS * DAY;
}

/** A group_share link whose dinner is more than GROUP_RETURN_DAYS old. */
export function isGroupReturn(createdAt: string, now = Date.now()): boolean {
  const t = Date.parse(createdAt);
  return Number.isFinite(t) && now - t > GROUP_RETURN_DAYS * DAY;
}

/** /r/NAME landings, for restaurant table tents and QR codes. */
export function restaurantFromLanding(landing: unknown): string {
  const m = /^\/r\/([^/?#]{1,60})/.exec(str(landing));
  return m ? decodeURIComponent(m[1]).toLowerCase() : '';
}

const isRestaurantCode = (c: NonNullable<AttributionInput['code']>) =>
  c.kind === 'restaurant' || c.utm?.source === 'restaurant' || c.utm?.medium === 'restaurant';

/** The one source for a new payer. First match wins, in the order Growth set. */
export function attribute(input: AttributionInput): Attribution {
  const ref = input.ref || {};
  const now = input.now ?? Date.now();
  const src = cleanSrc(ref.src);
  const utm = str(ref.utm_source).toLowerCase();
  const code = input.code || null;

  // Any group_share link (the dinner link, or a "Start a split" link carrying from= too) whose dinner
  // is more than 7 days old is the group coming back to the thread: group_thread, ahead even of the
  // table loop. A same-week group_share link falls through to the usual order, so a fresh
  // "Start a split" link is still table_loop. The dinner's created time is looked up on the server.
  if (input.groupShare && isGroupReturn(input.groupShare.createdAt, now)) return { source: 'group_thread', detail: input.groupShare.code };
  if (input.from && inLoopWindow(input.from.createdAt, now)) return { source: 'table_loop', detail: input.from.code };
  // A link that says it's the share card counts as the share card, even when it also carries the
  // payer's code. The code stays on the split, so referral credit (keyed by code) is unchanged.
  if (src === 'share_card') return { source: 'share_card', detail: code?.code || str(ref.utm_campaign) };
  if (code?.kind === 'referral') return { source: 'referral', detail: code.code };
  // Intended: ?src=creator with no valid creator code can't be credited to anyone, so it falls
  // through to utm/direct below. Only a real creator code counts as the creator source.
  if (code?.kind === 'creator') return { source: 'creator', detail: code.code };
  const place = restaurantFromLanding(ref.landing);
  if (code && isRestaurantCode(code)) return { source: 'restaurant', detail: code.code };
  if (place) return { source: 'restaurant', detail: place };
  if (src === 'restaurant' || utm === 'restaurant') return { source: 'restaurant', detail: str(ref.utm_campaign) };
  if (code && (code.kind === 'promo' || code.kind === 'campaign')) return { source: 'promo', detail: code.code };
  if (utm === 'share_card' || src === 'share_card') return { source: 'share_card', detail: str(ref.utm_campaign) };
  // Our own receipt or free-split reminder text. Ahead of returning_friend: the text is what brought them back.
  if (src === 'sms_receipt' || src === 'sms_reminder') return { source: 'sms', detail: src };
  if (input.wasFriend) return { source: 'returning_friend', detail: '' };
  if (utm) return { source: 'utm', detail: utm };
  return { source: 'direct', detail: '' };
}

/** When the phone first showed up, from the first-touch time it sent; never later than now. */
export function firstSeen(ref: Record<string, unknown> | undefined, fallback: string, now = Date.now()): string {
  const t = Date.parse(str(ref?.at));
  return Number.isFinite(t) && t <= now ? new Date(t).toISOString() : fallback;
}

export const daysBetween = (fromIso: string, now = Date.now()) => Math.max(0, Math.floor((now - Date.parse(fromIso)) / DAY));

type Share = Pick<PersonShare, 'current' | 'paid' | 'forgiven' | 'due'>;

/** Friends have their totals: locked (a split from before lock-in has no flag and counts as locked) or closed. */
export const totalsSent = (s: Pick<Split, 'locked' | 'status'>): boolean => s.locked !== false || s.status === 'closed';

/**
 * One friend is paid back: paid in full, let off, or nothing to pay. The board's rule (isSquare in
 * money.ts). A $0 share only counts once totals are sent: before that they may still pick.
 */
export function paidBack(share: Share | undefined, totalsSent: boolean): boolean {
  return !!share && isSquare(share) && (totalsSent || !nothingToPay(share));
}

/** Every friend is paid back (see paidBack). Needs at least one friend. */
export function fullyPaidBack(people: Array<{ isPayer?: boolean; id: string }>, share: (id: string) => Share | undefined, totalsSent: boolean): boolean {
  const friends = people.filter((p) => !p.isPayer);
  return friends.length > 0 && friends.every((p) => paidBack(share(p.id), totalsSent));
}
